'use client';

import { useState } from 'react';
import { ShieldCheck, Sparkles, Gauge, EyeOff, ArrowRight, ServerCog, Globe, Code2, Lock, Shield } from 'lucide-react';
import Link from 'next/link';
import { SHIELD_EMBED_URL } from '@/lib/shield/constants';

type DeployKey = 'shield' | 'proxy' | 'worker' | 'sdk';

const deployTabs: {
  key: DeployKey;
  label: string;
  hint: string;
  icon: typeof ServerCog;
  lines: { text: string; tone?: 'cmd' | 'kw' | 'str' | 'fn' | 'muted' | 'highlight' }[];
}[] = [
  {
    key: 'shield',
    label: 'Website Shield',
    hint: 'Any site · paste in <head> · free',
    icon: Shield,
    lines: [
      { text: '<script src="' + SHIELD_EMBED_URL + '"', tone: 'cmd' },
      { text: '  data-site-id="site_..." defer></script>', tone: 'muted' },
      { text: 'Trust badge + link checks · no backend', tone: 'highlight' },
    ],
  },
  {
    key: 'proxy',
    label: 'No-code proxy',
    hint: 'Any backend · zero code changes',
    icon: ServerCog,
    lines: [
      { text: '$ docker run ghcr.io/niceappspl/silker-proxy', tone: 'cmd' },
      { text: '  -e SILKER_TARGET=http://your-app:3000', tone: 'muted' },
      { text: 'Reverse proxy for any stack - PHP, Go, Java…', tone: 'highlight' },
    ],
  },
  {
    key: 'worker',
    label: 'Cloudflare Worker',
    hint: 'Beta · edge, any domain',
    icon: Globe,
    lines: [
      { text: '$ wrangler deploy', tone: 'cmd' },
      { text: 'Runs inside your Cloudflare zone', tone: 'muted' },
      { text: 'Blocks threats at the edge, before origin', tone: 'highlight' },
    ],
  },
  {
    key: 'sdk',
    label: 'SDK',
    hint: 'One line · Node, Next.js',
    icon: Code2,
    lines: [
      { text: '$ npm i @silker-ai/agent', tone: 'cmd' },
      { text: "Silker.init({ apiKey: SILKER_API_KEY })", tone: 'fn' },
      { text: 'Inbound + outbound traffic protected', tone: 'highlight' },
    ],
  },
];

const blockedFeed = [
  { tag: 'PROMPT INJECTION', detail: 'Instruction override on /api/agent', ms: '4ms' },
  { tag: 'DATA LEAK', detail: 'API key in outbound response blocked', ms: '2ms' },
  { tag: 'IDOR', detail: 'Cross-tenant access on /orders/:id', ms: '6ms' },
];

function lineClass(tone?: string): string {
  switch (tone) {
    case 'cmd':
      return 'text-neutral-500';
    case 'kw':
      return 'text-purple-400';
    case 'str':
      return 'text-green-400';
    case 'fn':
      return 'text-neutral-200';
    case 'highlight':
      return 'text-emerald-400';
    default:
      return 'text-neutral-400';
  }
}

export default function HeroSection() {
  const [active, setActive] = useState<DeployKey>('shield');
  const current = deployTabs.find((t) => t.key === active) ?? deployTabs[0];

  return (
    <section className="pb-12 relative bg-black min-h-[90vh] flex flex-col justify-center overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-rose-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="sm:px-6 lg:px-8 sm:pt-24 max-w-5xl mx-auto pt-16 px-4">

        <div className="flex flex-col items-center gap-4 mb-8">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-rose-400">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75"></span>
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rose-500"></span>
            </span>
            Beta
          </span>
          <div className="flex items-center gap-4">
            <div className="h-[1px] w-8 bg-gradient-to-r from-transparent to-rose-500/50"></div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-rose-500" />
              <span className="text-sm font-medium text-neutral-400 tracking-wide">
                Silker AI · Runtime application firewall · Shield, proxy, or SDK
              </span>
            </div>
            <div className="h-[1px] w-8 bg-gradient-to-l from-transparent to-rose-500/50"></div>
          </div>
        </div>

        <h1 className="sm:text-6xl md:text-7xl text-4xl font-bold tracking-tight text-center mb-6 max-w-full mx-auto leading-[1.1]">
          <span className="text-white block">Stop the attacks your</span>
          <span className="block mt-2 bg-gradient-to-r from-rose-500 via-rose-400 to-rose-300 bg-clip-text text-transparent">firewall can&apos;t even see.</span>
        </h1>

        <p className="sm:text-lg text-base text-neutral-400 text-center mb-8 max-w-2xl mx-auto leading-relaxed">
          <span className="text-white font-semibold">Silker AI</span> is an AI-native security layer that blocks <span className="text-white font-medium">prompt injection</span>, <span className="text-white font-medium">data leaks</span> and <span className="text-white font-medium">app-logic attacks</span> your WAF misses - in <span className="text-white font-medium">&lt;10ms</span>. Paste a script for any website, deploy a proxy for full runtime protection, or add one line of SDK code.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-10 text-xs sm:text-sm text-neutral-400 font-medium">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
            Prompt Injection Defense
          </div>
          <div className="flex items-center gap-2">
            <EyeOff className="w-3.5 h-3.5 text-emerald-500" />
            Data Leak Prevention
          </div>
          <div className="flex items-center gap-2">
            <Gauge className="w-3.5 h-3.5 text-emerald-500" />
            &lt;10ms Added Latency
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-emerald-500" />
            Runs On Your Infra
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
          <Link
            href="#scan"
            className="inline-flex items-center justify-center h-12 px-8 rounded-none bg-white text-black text-sm font-semibold hover:bg-neutral-200 transition-colors gap-2 min-w-[180px]"
          >
            Scan your app - free
            <ArrowRight className="w-4 h-4" />
          </Link>

          <Link
            href="/shield"
            className="inline-flex items-center justify-center h-12 px-8 rounded-none bg-transparent border border-neutral-800 text-white text-sm font-medium hover:bg-white/5 transition-colors gap-2 min-w-[160px]"
          >
            <span className="text-neutral-300">Website Shield</span>
          </Link>
        </div>

        <div className="mt-16 mx-auto max-w-2xl bg-black/80 border border-white/10 rounded-none p-4 backdrop-blur-xl shadow-2xl text-left relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-rose-500/20 to-purple-500/20 rounded-none blur opacity-50 group-hover:opacity-75 transition duration-500"></div>
          <div className="relative bg-neutral-900/90 rounded-none p-4 border border-white/5">

            <div className="flex flex-wrap items-center gap-1.5 mb-4 border-b border-white/5 pb-3">
              {deployTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = tab.key === active;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActive(tab.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-none text-xs font-medium transition-colors border ${
                      isActive
                        ? 'bg-rose-500/10 border-rose-500/30 text-white'
                        : 'bg-transparent border-white/5 text-neutral-400 hover:text-white hover:border-white/10'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="mb-1 text-[11px] text-neutral-500 font-mono">{current.hint}</div>
            <div className="font-mono text-sm space-y-1.5 min-h-[88px]">
              {current.lines.map((line, i) => (
                <div key={i} className={lineClass(line.tone)}>
                  {line.tone === 'highlight' ? <span className="text-emerald-400">&#10003; </span> : null}
                  {line.text}
                </div>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-white/5 space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest text-neutral-600 mb-2">Live · blocked in production</div>
              {blockedFeed.map((b, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-none bg-rose-500/10 border border-rose-500/20 text-rose-300 font-mono text-[10px] shrink-0">
                    {b.tag}
                  </span>
                  <span className="text-neutral-400 truncate">{b.detail}</span>
                  <span className="text-neutral-600 font-mono ml-auto shrink-0">{b.ms}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
